/**
 * Building bake (Task 8): kit compile cache + PURE world-transform expansion.
 *
 * Two halves, one file:
 *  - PURE (node-testable, no THREE object in the code path):
 *      compiledKit()        — memoized compileAsset() over every BUILDING_KIT doc
 *      planDoorSide()       — record (yaw, doorYaw) → building-local door side
 *                             (lives in shared/buildings/plan.ts since F1.4 so
 *                             the server can regenerate plans; re-exported here
 *                             to keep the client import surface stable)
 *      buildingInstances()  — Float64Array building records → flat BuildingInstance[]
 *  - THREE-dependent:
 *      bakeBuildingMeshes() — instances → InstancedMesh per geometry+glass bucket
 *
 * World-transform contract (BINDING):
 *   M_world = M_building(basis, record centre)
 *           × M_place(x, y, z, rotY quarter-turn, tile scale)
 *           × compiled.transform
 * with the building basis reconstructed EXACTLY as buildingKitLayout does it
 * (renderer.ts ~1160): local x = W (yaw heading in the tangent frame), local
 * y = U (outward radial), local z = D = W × U — the RIGHT-HANDED cross order,
 * det +1 (the V2 finding: a left-handed triple tilts/inverts the building).
 * Plan-local axes map 1:1 onto (W, U, D): plan x → W, plan y → U, plan z → D,
 * so plan side 0 (+z) faces +D, side 2 (+x) faces +W.
 *
 * Determinism: same Float64Array → byte-identical instance list. Buildings in
 * record order; per building modules → trims → greebles in plan order; per
 * placement the compiled instances in doc order. No Map iteration feeds output
 * order except the insertion-ordered group map in the (render-only) bake half.
 */
import * as THREE from "three";
import {frontierForRecord,frontierLocal,recordFrame} from '../../../../shared/src/worldgen/frontierBuildings';
import {proceduralLathe} from '../../assets/proceduralLathe';
import { addStylizedShading } from "../stylizedShading";
import {
  BUILDING_ARCHETYPES,
  BUILDING_KIT,
  BUILDING_STRIDE,
  buildingProgramForRecord,
  buildingProgramPalette,
  type BuildingProgram,
  compileAsset,
  interiorEligible,
  LEVEL_H_M,
  planDoorSide,
  ROT_DX, ROT_DZ,
  SIDE_ROT,
  USE_INN, USE_MARKET_HALL, USE_DEPOT, USE_SMITHY, USE_WORKSHOP,
  USE_APOTHECARY, USE_HALL, USE_ADMIN, USE_COURTHOUSE, USE_GUILDHALL,
  USE_GARRISON, USE_WATCHTOWER, USE_PRISON, USE_LOOM_GATE,
  USE_PRODUCTION, USE_MILL, USE_STABLE, USE_WAREHOUSE, USE_FUEL_SHED,
  USE_TEMPLE, USE_CHAPEL, USE_BASILICA, USE_SHRINE, USE_ACADEMY,
  USE_BATHHOUSE, USE_THEATER, USE_ARENA,
  USE_HARBOR, USE_DOCKYARD, USE_STARPORT,
  USE_EMBASSY, USE_PALACE, USE_HEADMAN, USE_LOOM_NEXUS,
  type BuildingArchetype,
  type CompiledAsset,
  type CompiledInstance,
} from "@hyperlight/shared";

// Moved to shared (F1.4) for phase-2 server-side plan regeneration; re-exported
// so existing client imports (tests, future callers) keep working unchanged.
export { planDoorSide };

// ── plain-array mat4 helpers (16 numbers, column-major — the same convention as
//    shared/proceduralAssetCompile.ts, whose mat4Multiply is module-private; a
//    local copy keeps the pure path free of any shared-export widening) ────────

/** out = a × b, both 16-element column-major. */
function mat4Multiply(a: number[], b: number[]): number[] {
  const out = new Array<number>(16);
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      out[col * 4 + row] =
        a[row] * b[col * 4] + a[4 + row] * b[col * 4 + 1]
        + a[8 + row] * b[col * 4 + 2] + a[12 + row] * b[col * 4 + 3];
    }
  }
  return out;
}

/** Exact quarter-turn cos/sin tables — no float trig drift on placement rots. */
const QCOS = [1, 0, -1, 0] as const;
const QSIN = [0, 1, 0, -1] as const;

/** M_place = T(x,y,z) · RotY(rot·π/2) · S(sx, 1, sz), built directly (exact). */
function placeMatrix(x: number, y: number, z: number,
  rot: 0 | 1 | 2 | 3, sx: number, sz: number): number[] {
  const c = QCOS[rot], s = QSIN[rot];
  // RotY columns (THREE convention, matches shared mat4RotationAxis "y"):
  // col0 = (c, 0, -s), col2 = (s, 0, c) — rot 1 carries local +z to +x.
  return [
    c * sx, 0, -s * sx, 0,
    0, 1, 0, 0,
    s * sz, 0, c * sz, 0,
    x, y, z, 1,
  ];
}

// ── tangent frame + building basis (duplicated from renderer.ts, kept pure) ───

/**
 * Right-handed tangent frame at planet-local (px,py,pz): up = outward radial,
 * t1/t2 span the tangent plane; t1 × t2 = up. Identical to the renderer's
 * `tangentFrame` (module-private there) — the [x,y,z,yaw,…] orientation
 * contract requires bit-agreement with buildingKitLayout's reconstruction.
 */
export function tangentFrame(px: number, py: number, pz: number): { up: number[]; t1: number[]; t2: number[] } {
  const l = Math.hypot(px, py, pz) || 1;
  const up = [px / l, py / l, pz / l];
  const rx = Math.abs(up[1]) < 0.9 ? 0 : 1;
  const ry = Math.abs(up[1]) < 0.9 ? 1 : 0;
  let t1x = ry * up[2] - 0 * up[1];
  let t1y = 0 * up[0] - rx * up[2];
  let t1z = rx * up[1] - ry * up[0];
  const l1 = Math.hypot(t1x, t1y, t1z) || 1;
  t1x /= l1; t1y /= l1; t1z /= l1;
  const t2x = up[1] * t1z - up[2] * t1y;
  const t2y = up[2] * t1x - up[0] * t1z;
  const t2z = up[0] * t1y - up[1] * t1x;
  return { up, t1: [t1x, t1y, t1z], t2: [t2x, t2y, t2z] };
}

// ── kit compile cache ─────────────────────────────────────────────────────────

let kitCache: ReadonlyMap<string, CompiledAsset> | null = null;

/** All BUILDING_KIT docs compiled once per session, keyed by kit id. */
export function compiledKit(): ReadonlyMap<string, CompiledAsset> {
  if (!kitCache) {
    const m = new Map<string, CompiledAsset>();
    for (const [id, entry] of BUILDING_KIT) m.set(id, compileAsset(entry.doc));
    kitCache = m;
  }
  return kitCache;
}

// ── colour model ──────────────────────────────────────────────────────────────

/**
 * Archetype colour ramps: 4 near-unity RGB multipliers per archetype, indexed
 * by plan.paletteIndex, applied over the kit palette colour. Kept close to 1 so
 * metal trim / rust accents shift hue without crushing albedo (the kit palette
 * carries the base read; ramps carry per-building variety). Taste choices:
 * habitation stays in the neutral plaster family, industrial/outpost lean into
 * the rust-orange / drab-olive accents of the reference sheet, lab bleaches
 * clean, marketplace gets the widest hue spread (awning-town variety).
 */
// Widened to ~±18% (was ±10%) so the four paletteIndex variants read as DISTINCT
// weathered tones on the mid-value adobe base, not a uniform pale wash — the
// "grounded & weathered" pass. Kept ≤ ~1.15 so the rust accent (material 2) and
// metal trim (1) shift hue without being crushed or blown out. Per-archetype
// character: habitation weathered plaster/tan/adobe; watchtower cool stone;
// industrial rust-and-drab; lab bleached (slightly cool); marketplace widest
// hue spread; outpost olive-drab military.
const ARCHETYPE_RAMPS: Record<BuildingArchetype, readonly [number, number, number][]> = {
  habitation: [[1.02, 0.99, 0.93], [1.13, 1.0, 0.83], [0.85, 0.87, 0.9], [1.06, 0.9, 0.76]],
  watchtower: [[0.88, 0.92, 1.02], [0.82, 0.88, 0.98], [0.98, 0.98, 1.05], [0.9, 0.93, 1.0]],
  industrial: [[1.12, 0.9, 0.72], [0.9, 0.82, 0.78], [1.15, 0.98, 0.76], [0.85, 0.84, 0.82]],
  lab: [[1.05, 1.06, 1.08], [0.96, 1.02, 1.08], [1.08, 1.08, 1.02], [0.92, 0.98, 1.05]],
  marketplace: [[1.13, 0.94, 0.8], [0.86, 0.98, 1.06], [1.08, 1.05, 0.85], [0.98, 0.9, 1.04]],
  outpost: [[0.9, 0.9, 0.78], [0.82, 0.85, 0.76], [0.96, 0.92, 0.82], [0.85, 0.83, 0.8]],
};

/** Age tint (record field 7, 0 oldest → 1 newest) as NORMALIZED multipliers —
 * a warm sun-baked → neutral cool-STONE swing over the adobe base. The cool end
 * is a DESATURATED warm-neutral (not blue): the world is warm olive/tan, so an
 * aged building reads as bleached stone, never cold blue-grey (which popped as an
 * outlier against the terrain). Restores a continuous per-building age axis
 * without the near-unity flatness that made every wall identical; kept moderate
 * so it layers with the wider archetype ramps without washing the accents. */
const AGE_WARM: readonly number[] = [1.06, 0.97, 0.85]; // oldest — sun-baked ochre
const AGE_COOL: readonly number[] = [0.96, 0.96, 0.97]; // newest — bleached neutral stone

// 2026-07-30 colour pass, AMPLIFIED same day (user: "significantly more
// colourful and contrasted — both in and out"). BUILD_TINTS: a per-building
// hue FAMILY layered over the archetype ramp + age tint, keyed off the
// buildingSeed — terracotta / sage / cornflower / ochre / rose / teal / cream,
// bold enough that neighbouring houses read as different paints, not shades of
// one plaster. FURN_TINTS: the matching per-building furniture families —
// `int-*` pieces take THIS instead of the wall multipliers, so a bleached-lab
// archetype ramp never washes out the fittings inside it.
const BUILD_TINTS: readonly [number, number, number][] = [
  [1, 1, 1], [1.25, 0.9, 0.72], [0.88, 1.08, 0.85], [0.8, 0.92, 1.22],
  [1.22, 1.05, 0.7], [1.2, 0.85, 0.9], [0.75, 1.02, 1.08], [1.12, 1.08, 0.92],
];
const UNIT_TINT: readonly [number, number, number] = [1, 1, 1]; // wall art: authored colours
// FLOOR SET (2026-07-30 "a standard set of floors, separate from the walls"):
// four ABSOLUTE floor colours — white / grey / dark wood / light wood — chosen
// per building (seed>>17) and applied to floor slabs + interior stairs
// REPLACING the doc base colour and every wall multiplier, so floors never
// inherit the house's wall pastel. Door leaves reuse the same family
// (buildingDoors exports the colour) so doors match the house woodwork.
const FLOOR_COLS: readonly [number, number, number][] = [
  [0.85, 0.84, 0.82], [0.5, 0.51, 0.53], [0.3, 0.21, 0.14], [0.74, 0.56, 0.36],
];
export function floorColorFor(seed: number): readonly [number, number, number] {
  return FLOOR_COLS[(seed >>> 17) % FLOOR_COLS.length];
}
// Furniture reads as FURNITURE, not wall paint (2026-07-30 "furniture should
// not simply be the same pastel colours as the walls"): per-building WOOD
// STAIN on the wood materials (0 warm / 4 dark) and a separate per-building
// TEXTILE hue on fabric/linen (2 / 5); metal, glass and foliage keep their
// authored colours.
const STAIN_TINTS: readonly [number, number, number][] = [
  [1, 1, 1], [1.15, 0.72, 0.58], [0.72, 0.6, 0.5],
  [1.28, 1.02, 0.62], [0.5, 0.47, 0.45], [1.2, 0.68, 0.6],
];
const TEXTILE_TINTS: readonly [number, number, number][] = [
  [1, 1, 1], [1.2, 0.7, 0.7], [0.65, 0.75, 1.3],
  [1.25, 1.1, 0.55], [0.7, 1.1, 0.7], [1.15, 0.72, 1.2],
];
// Per-building ROOF and ACCENT families, applied by material index (4 = roof
// slate, 2 = rust accent) on top of the wall mult — warm-brown / blue-slate /
// moss / terracotta / violet-slate roofs; teal / gold / violet / green /
// crimson flourishes. Deliberately the boldest layer: roofs and trim are where
// a street's colour reads from a distance.
const ROOF_TINTS: readonly [number, number, number][] = [
  [1, 1, 1], [1.4, 0.95, 0.65], [0.72, 0.95, 1.35], [0.78, 1.2, 0.72],
  [1.55, 0.78, 0.6], [1.05, 0.9, 1.3],
];
const ACCENT_TINTS: readonly [number, number, number][] = [
  [1, 1, 1], [0.45, 1.1, 1.4], [1.3, 1.15, 0.45], [0.95, 0.62, 1.4],
  [0.6, 1.25, 0.7], [1.3, 0.75, 0.8],
];

// ── pure expansion ────────────────────────────────────────────────────────────

/** Pure (no THREE): one renderable instance = unique-geometry key + world matrix. */
export interface BuildingInstance {
  footstepSurface?: import('../../audio/footsteps').FootstepSurface;
  /** Building record index this instance belongs to (Task 9): the renderer's
   * district tint is derived from the BUILDING's position (districtAt over the
   * region graph), which the pure path deliberately knows nothing about — the
   * index lets the renderer multiply `color` per building after expansion,
   * keeping this function pure and node-testable. */
  b: number;
  /** Instancing bucket — includes profile/deforms because they change VERTEX
   * data (two same-param boxes with different tapers must not share geometry;
   * same convention as proceduralAssetView's geometryKey). */
  geomKey: string;
  kind: CompiledInstance["kind"];
  params: number[];
  profile?: [number, number][];
  faceting?: number;
  deforms?: CompiledInstance["deforms"];
  glass: boolean;              // material 3 → night-emissive bucket
  matrix: number[];            // 16, column-major, planet-local
  color: [number, number, number]; // kit palette × archetype ramp × age tint
}

function geomKeyOf(inst: CompiledInstance): string {
  return JSON.stringify([inst.kind, inst.params, inst.profile ?? null, inst.deforms ?? null,...(inst.faceting===undefined?[]:[inst.faceting])]);
}

/** One doorway of a hollow building (2026-07-30 auto-doors): planet-local
 *  door-base centre ON the wall plane, plus the outward normal and the
 *  lateral (slide) axis. Pure — recomputed per region at build time. */
export interface DoorPlacement {
  x: number; y: number; z: number;
  ox: number; oy: number; oz: number;   // outward (out of the building)
  ax: number; ay: number; az: number;   // lateral along the wall (slide axis)
  ux: number; uy: number; uz: number;   // up (radial)
  sillM: number;                        // shell floorTop (leaf bottom sits here)
  /** The building's floor-family colour (white/grey/woods) — door leaves are
   *  painted with it so doors match the house's floor/stair woodwork. */
  tint: readonly [number, number, number];
}

/** Extract every hollow building's doorway from a region's records. */
export function buildingDoors(buildings: Float64Array): DoorPlacement[] {
  const out: DoorPlacement[] = [];
  const SDX = [0, 0, 1, -1], SDZ = [1, -1, 0, 0];
  const nB = Math.floor(buildings.length / BUILDING_STRIDE);
  for (let b = 0; b < nB; b++) {
    const o = b * BUILDING_STRIDE;
    if (frontierForRecord(buildings,o)) continue; // authored openings, no legacy sliding leaves
    if (!interiorEligible(buildings[o + 15], buildings[o + 17])) continue;
    const program = buildingProgramForRecord(buildings,o), plan = program.plan;
    const dw = plan.shell?.walls.find((w) => w.kind === "door");
    if (plan.shell === null || dw === undefined) continue;
    const bx = buildings[o], by = buildings[o + 1], bz = buildings[o + 2];
    const f = tangentFrame(bx, by, bz);
    const yaw = buildings[o + 3];
    const cyw = Math.cos(yaw), syw = Math.sin(yaw);
    const W = [
      f.t1[0] * cyw + f.t2[0] * syw,
      f.t1[1] * cyw + f.t2[1] * syw,
      f.t1[2] * cyw + f.t2[2] * syw,
    ];
    const D = [
      W[1] * f.up[2] - W[2] * f.up[1],
      W[2] * f.up[0] - W[0] * f.up[2],
      W[0] * f.up[1] - W[1] * f.up[0],
    ];
    const U2 = plan.unit / 2 - 0.2; // wall centre plane (SHELL_WALL_T/2 inset)
    const lx = dw.x + SDX[dw.side] * U2;
    const lz = dw.z + SDZ[dw.side] * U2;
    const outx = W[0] * SDX[dw.side] + D[0] * SDZ[dw.side];
    const outy = W[1] * SDX[dw.side] + D[1] * SDZ[dw.side];
    const outz = W[2] * SDX[dw.side] + D[2] * SDZ[dw.side];
    // Slide axis = up × out — GUARANTEED right-handed with (up, out). Taking
    // the raw W/D axis made sides 1/3 a LEFT-handed triple, and a rotation
    // built from that reflection basis skewed half the door leaves sideways
    // ("completely misaligned — pitch/skew off" play report). Slide sign is
    // irrelevant (panels move ±symmetrically).
    const ax2 = f.up[1] * outz - f.up[2] * outy;
    const ay2 = f.up[2] * outx - f.up[0] * outz;
    const az2 = f.up[0] * outy - f.up[1] * outx;
    out.push({
      x: bx + W[0] * lx + D[0] * lz,
      y: by + W[1] * lx + D[1] * lz,
      z: bz + W[2] * lx + D[2] * lz,
      ox: outx, oy: outy, oz: outz,
      ax: ax2, ay: ay2, az: az2,
      ux: f.up[0], uy: f.up[1], uz: f.up[2],
      sillM: plan.shell.floorTopM,
      tint: floorColorFor(buildings[o + 14] >>> 0),
    });
  }
  return out;
}

/** One resolved interior anchor in PLANET-LOCAL space (P-B2 NPC life). */
export interface WorldAnchor {
  kind: string;
  /** Planet-local position at the anchor's FLOOR (feet height). */
  pos: [number, number, number];
  /** Planet-local facing direction (the anchor's rot, e.g. out from a wall). */
  face: [number, number, number];
  /** Planet-local direction of the PIECE's local +x — its LONG axis for the
   *  wide pieces (bed, counter, bench). Sleepers lie along this (2026-07-31:
   *  residents slept ACROSS their beds because only `face` was published, and
   *  `face` points out of the wall, i.e. across a wall-hugging bed). The
   *  int-bed doc puts its headboard/pillow at local −x, so head = −right. */
  right: [number, number, number];
  level: number;
}

/** Interior anchors of the building on `parcelIndex`, in planet-local space —
 *  the NPC-life join surface (work→counter, sleep→bed, …). Pure f(records);
 *  callers cache. Null when the parcel has no interior-eligible building. */
export function buildingInteriorAnchors(buildings: Float64Array, parcelIndex: number): WorldAnchor[] | null {
  const nB = Math.floor(buildings.length / BUILDING_STRIDE);
  for (let b = 0; b < nB; b++) {
    const o = b * BUILDING_STRIDE;
    if (Math.round(buildings[o + 16]) !== parcelIndex) continue;
    const frontier=frontierForRecord(buildings,o);
    if(frontier){
      const frame=recordFrame(buildings,o),q=frontier.quarterTurn*Math.PI/2;
      return frontier.plan.slots.map(slot=>{
        const yaw=slot.heading+q,fx=Math.sin(yaw),fz=Math.cos(yaw);
        return {kind:slot.activity==='serve'?'counter':slot.activity==='browse'?'shelf':'workbench',pos:frame.point(frontierLocal(frontier,slot.position)),
          face:frame.width.map((v,i)=>v*fx+frame.depth[i]*fz) as [number,number,number],
          right:frame.width.map((v,i)=>v*fz-frame.depth[i]*fx) as [number,number,number],level:0};
      });
    }
    if (!interiorEligible(buildings[o + 15], buildings[o + 17])) return null;
    const program = buildingProgramForRecord(buildings,o), plan = program.plan;
    const ip = program.interior;
    if (ip === null || plan.shell === null) return null;
    const bx = buildings[o], by = buildings[o + 1], bz = buildings[o + 2];
    const f = tangentFrame(bx, by, bz);
    const yaw = buildings[o + 3];
    const cyw = Math.cos(yaw), syw = Math.sin(yaw);
    const W = [
      f.t1[0] * cyw + f.t2[0] * syw,
      f.t1[1] * cyw + f.t2[1] * syw,
      f.t1[2] * cyw + f.t2[2] * syw,
    ];
    const D = [
      W[1] * f.up[2] - W[2] * f.up[1],
      W[2] * f.up[0] - W[0] * f.up[2],
      W[0] * f.up[1] - W[1] * f.up[0],
    ];
    const H = LEVEL_H_M;
    const out: WorldAnchor[] = [];
    for (const a of ip.anchors) {
      const yOff = a.level === 0 ? ip.floorTopM : a.level * H;
      const fx = ROT_DX[a.rot], fz = ROT_DZ[a.rot]; // rot → the facing dir (+z rotated)
      // The piece's local +x under the same quarter turn: rotating (1,0) about
      // +y by θ gives (cosθ, −sinθ), and (ROT_DX, ROT_DZ) = (sinθ, cosθ).
      const rx = ROT_DZ[a.rot], rz = -ROT_DX[a.rot];
      out.push({
        kind: a.kind,
        pos: [
          bx + W[0] * a.x + D[0] * a.z + f.up[0] * yOff,
          by + W[1] * a.x + D[1] * a.z + f.up[1] * yOff,
          bz + W[2] * a.x + D[2] * a.z + f.up[2] * yOff,
        ],
        face: [
          W[0] * fx + D[0] * fz,
          W[1] * fx + D[1] * fz,
          W[2] * fx + D[2] * fz,
        ],
        right: [
          W[0] * rx + D[0] * rz,
          W[1] * rx + D[1] * rz,
          W[2] * rx + D[2] * rz,
        ],
        level: a.level,
      });
    }
    return out;
  }
  return null;
}

// 2026-07-30: projecting door signs — the building's USE picks the icon so a
// banner "indicates what kind of building it is attached to" (user ask).
// Homes hang no trade sign.
const SIGN_FOR_USE: ReadonlyMap<number, string> = new Map([
  [USE_INN, "sign-mug"],
  [USE_MARKET_HALL, "sign-coin"], [USE_DEPOT, "sign-coin"], [USE_HARBOR, "sign-anchor"],
  [USE_SMITHY, "sign-hammer"], [USE_WORKSHOP, "sign-hammer"], [USE_GUILDHALL, "sign-hammer"],
  [USE_DOCKYARD, "sign-anchor"], [USE_STARPORT, "sign-emblem"], [USE_FUEL_SHED, "sign-crate"],
  [USE_APOTHECARY, "sign-flask"],
  [USE_HALL, "sign-emblem"], [USE_ADMIN, "sign-emblem"],
  [USE_COURTHOUSE, "sign-emblem"], [USE_EMBASSY, "sign-emblem"], [USE_PALACE, "sign-emblem"],
  // 2026-07-31 style pass: every workplace type carries its trade sign.
  [USE_GARRISON, "sign-sword"], [USE_WATCHTOWER, "sign-sword"], [USE_PRISON, "sign-sword"],
  [USE_LOOM_GATE, "sign-sword"],
  [USE_PRODUCTION, "sign-sheaf"], [USE_MILL, "sign-sheaf"], [USE_STABLE, "sign-sheaf"],
  [USE_WAREHOUSE, "sign-crate"],
  [USE_TEMPLE, "sign-flame"], [USE_CHAPEL, "sign-flame"], [USE_BASILICA, "sign-flame"],
  [USE_SHRINE, "sign-flame"],
  [USE_ACADEMY, "sign-book"],
  [USE_BATHHOUSE, "sign-mask"], [USE_THEATER, "sign-mask"], [USE_ARENA, "sign-mask"],
]);

// ── 2026-07-31 style pass: TYPE FAMILIES ─────────────────────────────────────
// One at-a-glance accent colour per building family, replacing the per-seed
// accent roll on every non-home use — door frames, trim and flourishes tell
// you the trade from across the street. Homes keep their per-seed variety.
// 1 commerce · 2 craft · 3 civic · 4 faith · 5 military · 6 leisure · 7 agri
const USE_FAMILY: ReadonlyMap<number, number> = new Map([
  [USE_INN, 1], [USE_MARKET_HALL, 1], [USE_DEPOT, 1], [USE_HARBOR, 1],
  [USE_SMITHY, 2], [USE_WORKSHOP, 2], [USE_GUILDHALL, 2], [USE_WAREHOUSE, 2],
  [USE_DOCKYARD, 2], [USE_FUEL_SHED, 2], [USE_STARPORT, 2],
  [USE_HALL, 3], [USE_ADMIN, 3], [USE_COURTHOUSE, 3], [USE_EMBASSY, 3],
  [USE_PALACE, 3], [USE_ACADEMY, 3], [USE_HEADMAN, 3], [USE_LOOM_NEXUS, 3],
  [USE_TEMPLE, 4], [USE_CHAPEL, 4], [USE_BASILICA, 4], [USE_SHRINE, 4],
  [USE_GARRISON, 5], [USE_WATCHTOWER, 5], [USE_PRISON, 5], [USE_LOOM_GATE, 5],
  [USE_BATHHOUSE, 6], [USE_THEATER, 6], [USE_ARENA, 6],
  [USE_PRODUCTION, 7], [USE_MILL, 7], [USE_STABLE, 7],
]);
const FAMILY_ACCENTS: readonly [number, number, number][] = [
  [1, 1, 1],            // 0 home — unused (per-seed roll)
  [1.35, 1.1, 0.4],     // 1 commerce — gold
  [1.4, 0.65, 0.4],     // 2 craft — rust red
  [0.55, 0.85, 1.35],   // 3 civic — steel blue
  [1.3, 1.25, 0.95],    // 4 faith — white gold
  [0.5, 0.55, 0.62],    // 5 military — dark iron
  [1.1, 0.7, 1.35],     // 6 leisure — violet
  [0.7, 1.2, 0.55],     // 7 agriculture — green
];

// Dev-warn once-sets (module-level, per session). A typo'd id in a recipe would
// otherwise silently delete geometry (F3.1); a z-asymmetric `bend` under a
// mirror modifier would silently render wrong through the det<0 fix (F3.2).
// Both keep the resilient behaviour (skip / mirror) and only surface a signal.
const warnedUnknownKitIds = new Set<string>();
const warnedMirroredBend = new Set<string>();

/**
 * Expand building records (BUILDING_STRIDE floats each — see worldgen
 * structures.ts) into a flat, deterministic BuildingInstance list.
 * PURE: plain arrays end-to-end, runs in node without a renderer.
 */
export function buildingInstances(buildings: Float64Array): BuildingInstance[] {
  return expandBuildingInstances(buildings);
}

/** Local-space preview uses the exact production kit compiler and material pass. */
export function buildingProgramInstances(program: BuildingProgram): BuildingInstance[] {
  const i=program.request.input,records=new Float64Array(BUILDING_STRIDE);
  records.set([0,10000,0,Math.PI/2,i.wM,i.dM,i.hM,.25,10000,0,0,0,0,
    BUILDING_ARCHETYPES.indexOf(i.archetype),i.seed,program.request.use,0,i.floorRaiseM??0]);
  const instances=expandBuildingInstances(records,program);
  for(const instance of instances)instance.matrix[13]-=10000;
  return instances;
}

function expandBuildingInstances(buildings: Float64Array, resolved?: BuildingProgram): BuildingInstance[] {
  const out: BuildingInstance[] = [];
  const kit = compiledKit();
  const nB = Math.floor(buildings.length / BUILDING_STRIDE);
  for (let b = 0; b < nB; b++) {
    const o = b * BUILDING_STRIDE;
    if(!resolved&&frontierForRecord(buildings,o))continue; // rendered by the region-wide frontier batch
    const bx = buildings[o], by = buildings[o + 1], bz = buildings[o + 2];
    const yaw = buildings[o + 3];
    const tint = buildings[o + 7];
    const archetype: BuildingArchetype =
      BUILDING_ARCHETYPES[Math.round(buildings[o + 13])] ?? "habitation";
    const seed = buildings[o + 14] >>> 0;

    // Building basis — the EXACT buildingKitLayout construction (see header).
    const f = tangentFrame(bx, by, bz);
    const U = f.up;
    const cyw = Math.cos(yaw), syw = Math.sin(yaw);
    let wx = f.t1[0] * cyw + f.t2[0] * syw;
    let wy = f.t1[1] * cyw + f.t2[1] * syw;
    let wz = f.t1[2] * cyw + f.t2[2] * syw;
    const wl = Math.hypot(wx, wy, wz) || 1; wx /= wl; wy /= wl; wz /= wl;
    // depth = width × up (right-handed: x×y = z ⇒ det +1 — hard requirement)
    let dx = wy * U[2] - wz * U[1], dy = wz * U[0] - wx * U[2], dz = wx * U[1] - wy * U[0];
    const dl = Math.hypot(dx, dy, dz) || 1; dx /= dl; dy /= dl; dz /= dl;
    const mB = [wx, wy, wz, 0, U[0], U[1], U[2], 0, dx, dy, dz, 0, bx, by, bz, 1];

    const program = resolved ?? buildingProgramForRecord(buildings,o), plan = program.plan;
    const palette = buildingProgramPalette(program);

    // Per-building colour multiplier: archetype ramp at paletteIndex × age
    // tint × a subtle seed-keyed hue lean (colour pass).
    const ramp = ARCHETYPE_RAMPS[archetype][plan.paletteIndex] ?? [1, 1, 1];
    const bt = BUILD_TINTS[(seed >>> 3) % BUILD_TINTS.length];
    const t = Math.min(1, Math.max(0, tint));
    const mult: [number, number, number] = [
      ramp[0] * (AGE_WARM[0] + (AGE_COOL[0] - AGE_WARM[0]) * t) * bt[0],
      ramp[1] * (AGE_WARM[1] + (AGE_COOL[1] - AGE_WARM[1]) * t) * bt[1],
      ramp[2] * (AGE_WARM[2] + (AGE_COOL[2] - AGE_WARM[2]) * t) * bt[2],
    ];
    // Furniture takes its own per-building families, not the wall multipliers.
    const stainTint = STAIN_TINTS[(seed >>> 7) % STAIN_TINTS.length];
    const textileTint = TEXTILE_TINTS[(seed >>> 21) % TEXTILE_TINTS.length];
    const roofTint = ROOF_TINTS[(seed >>> 11) % ROOF_TINTS.length];
    // Non-home uses take their FAMILY accent (style pass); homes keep variety.
    const family = USE_FAMILY.get(buildings[o + 15]) ?? 0;
    const accentTint = family > 0 ? FAMILY_ACCENTS[family]
      : ACCENT_TINTS[(seed >>> 13) % ACCENT_TINTS.length];
    const floorCol = floorColorFor(seed);

    /** Emit every compiled instance of kit piece `id` placed at (x,y,z,rot),
     * with the module tile scale baked into M_place (1 for trims/greebles). */
    const emit = (id: string, x: number, y: number, z: number,
      rot: 0 | 1 | 2 | 3, wU = 0, dU = 0): void => {
      const entry = BUILDING_KIT.get(id);
      const compiled = kit.get(id);
      if (!entry || !compiled) {
        // unknown piece: skip (render resilience), but a typo'd id in a recipe
        // silently deletes geometry — warn once per id so it is not invisible.
        if (!warnedUnknownKitIds.has(id)) {
          warnedUnknownKitIds.add(id);
          console.warn(`[buildings] unknown kit id "${id}" — placement skipped (check the recipe)`);
        }
        return; // never throw mid-bake
      }
      // 2×2 / 2×1 tiles may exceed the doc footprint: scale the doc's LOCAL x/z
      // so the placed module spans wU×dU grid cells. Placement wU/dU are GRID
      // extents; odd rots swap which local axis covers which grid axis.
      let sx = 1, sz = 1;
      if (wU > 0 && dU > 0) {
        const [fpW, fpD] = entry.footprintUnits;
        const localW = rot % 2 === 0 ? wU : dU;
        const localD = rot % 2 === 0 ? dU : wU;
        sx = localW / fpW;
        sz = localD / fpD;
      }
      const mPB = mat4Multiply(mB, placeMatrix(x, y, z, rot, sx, sz));
      const mats = entry.doc.materials;
      const furnish = id.startsWith("int-") || id.startsWith("sign-");
      const isArt = id.startsWith("art-");
      // Floor pieces take the ABSOLUTE floor-set colour (white/grey/woods) —
      // no doc base, no wall mult — so floors read as their own surface.
      const isFloor = id === "shell-floor" || id === "shell-floor-upper" || id === "int-stair";
      for (const inst of compiled.instances) {
        // Material-family tints: furniture/signs take wood-stain (mats 0/4) or
        // textile (mats 2/5) families; wall art keeps its authored palette
        // untinted (the "fun" is in the composed colours); architecture takes
        // the wall mult, with roof (mat 4) and accent (mat 2) leaning further
        // per building.
        const pieceMult = isArt || isFloor ? UNIT_TINT
          : furnish ? (inst.material === 0 || inst.material === 4 ? stainTint
            : inst.material === 2 || inst.material === 5 ? textileTint : UNIT_TINT)
          : inst.material === 4 ? [mult[0] * roofTint[0], mult[1] * roofTint[1], mult[2] * roofTint[2]]
          : inst.material === 2 ? [mult[0] * accentTint[0], mult[1] * accentTint[1], mult[2] * accentTint[2]]
          : mult;
        const styled = palette !== 'existing' && !furnish && !isArt && !isFloor && inst.material !== 3;
        const paletteColors = palette === 'ochre'
          ? [0xc9a570,0x5e4b38,0x326597,0x223038,0xac5639]
          : [0xe5d9b9,0x6d5942,0x356caa,0x223038,0xb85b3b];
        const base = styled ? paletteColors[inst.material] ?? 0xd9c7a5 : mats[inst.material]?.color ?? 0xffffff;
        const linearStyle = styled ? new THREE.Color(base) : null;
        const r = linearStyle ? linearStyle.r : isFloor ? floorCol[0] : ((base >> 16) & 0xff) / 255;
        const g = linearStyle ? linearStyle.g : isFloor ? floorCol[1] : ((base >> 8) & 0xff) / 255;
        const bl = linearStyle ? linearStyle.b : isFloor ? floorCol[2] : (base & 0xff) / 255;
        const matrix = mat4Multiply(mPB, inst.transform);
        const geomKey = geomKeyOf(inst);
        // Mirror-modifier instances arrive as REFLECTIONS (det −1) — the lab
        // hides that behind DoubleSide materials; instancing must not. Every
        // kit primitive is symmetric under local z negation (boxes; y-radial
        // cylinders/cones/spheres/capsules; kit deforms are taper-only, which
        // preserves it), so M · diag(1,1,−1) renders the identical point set
        // as a PROPER rotation with restored front-face winding.
        const det = matrix[0] * (matrix[5] * matrix[10] - matrix[6] * matrix[9])
          - matrix[4] * (matrix[1] * matrix[10] - matrix[2] * matrix[9])
          + matrix[8] * (matrix[1] * matrix[6] - matrix[2] * matrix[5]);
        if (det < 0) {
          // The mirror-fix post-multiplies diag(1,1,−1); this is only valid
          // because every kit primitive is symmetric under local-z negation.
          // A `bend` deform is z-asymmetric, so a future kit doc with a bent
          // primitive under a mirror modifier would render wrong here. No kit
          // doc uses bend today (pinned in buildings-kit.test.mts), so this is
          // unreachable — warn once per geometry if that ever changes (F3.2).
          if (inst.deforms?.bend && !warnedMirroredBend.has(geomKey)) {
            warnedMirroredBend.add(geomKey);
            console.warn(`[buildings] mirrored bend deform is unsupported by the z-negate `
              + `fix (geom ${geomKey}) — the reflection will render wrong`);
          }
          matrix[8] = -matrix[8]; matrix[9] = -matrix[9]; matrix[10] = -matrix[10];
        }
        const bi: BuildingInstance = {
          b,
          geomKey,
          kind: inst.kind,
          params: [...inst.params],
          glass: inst.material === 3,
          footstepSurface: isFloor ? ((seed >>> 17) % FLOOR_COLS.length >= 2 ? 'wood' : 'stone')
            : mats[inst.material]?.metalness > .3 ? 'metal' : furnish && (inst.material === 0 || inst.material === 4) ? 'wood' : 'stone',
          matrix,
          color: styled ? [r * (1-t*.12), g * (1-t*.12), bl * (1-t*.12)] : [r * pieceMult[0], g * pieceMult[1], bl * pieceMult[2]],
        };
        if (inst.profile) bi.profile = inst.profile.map((p) => [p[0], p[1]] as [number, number]);
        if (inst.faceting !== undefined) bi.faceting = inst.faceting;
        if (inst.deforms) bi.deforms = { ...inst.deforms };
        out.push(bi);
      }
    };

    if (plan.shell !== null) {
      // Hollow shells replace the module docs (P3: EVERY storey when the
      // massing is box-family / multiFloor; specialty archetypes keep their
      // solid upper modules and crowns). The piece ids mirror plan.ts
      // shellCollisionBoxes one-for-one — collision and bake describe the
      // same geometry.
      const H = LEVEL_H_M;
      for (const m of plan.modules) {
        if (m.level === 0) continue;
        if (plan.shell.multiFloor && m.module !== "mod-dome") continue;
        emit(m.module, m.x, m.y, m.z, m.rot, m.wU, m.dU);
      }
      // The slab docs' local TOP is y = 0 — placing them at the floor plane
      // puts the walkable surface exactly at the collision slab top.
      for (const fl of plan.shell.floors) emit("shell-floor", fl.x, plan.shell.floorTopM, fl.z, 0);
      // +0.02: the storey-below wall panels TOP OUT exactly at L·H — the same
      // plane, same orientation as this slab's top, overlapping in the wall
      // band — and z-fought as a flickering strip wherever a massing step
      // exposes the ledge ("floors clip on second/third floors" play report).
      for (const fl of plan.shell.upperFloors) emit("shell-floor-upper", fl.x, fl.level * H + 0.02, fl.z, 0);
      for (const cap of plan.shell.caps) emit("shell-roof", cap.x, cap.level * H, cap.z, 0);
      for (const wl of plan.shell.walls) {
        const id = wl.kind === "door" ? "shell-wall-door"
          : wl.kind === "win" ? "shell-wall-win" : "shell-wall";
        emit(id, wl.x, 0, wl.z, SIDE_ROT[wl.side]);
      }
      for (const wl of plan.shell.upperWalls) {
        emit(wl.kind === "win" ? "shell-wall-win" : "shell-wall",
          wl.x, wl.level * H, wl.z, SIDE_ROT[wl.side]);
      }
      for (const st of plan.shell.stairs) emit("int-stair", st.x, st.level * H, st.z, st.rot);
      // Kind sign beside the entry (icon per USE), projecting off the door
      // wall at head height — offset along the wall clear of the frame.
      const signId = SIGN_FOR_USE.get(buildings[o + 15]);
      const dw = plan.shell.walls.find((w) => w.kind === "door");
      if (signId !== undefined && dw !== undefined) {
        // Anchor ON the wall plane (cell edge), offset along the wall clear
        // of the door frame; the sign doc projects along its local +z.
        const SDX = [0, 0, 1, -1], SDZ = [1, -1, 0, 0];
        const alongX = dw.side <= 1;
        emit(signId,
          dw.x + (alongX ? 1.45 : 0) + SDX[dw.side] * (plan.unit / 2),
          2.5,
          dw.z + (alongX ? 0 : 1.45) + SDZ[dw.side] * (plan.unit / 2),
          SIDE_ROT[dw.side]);
      }
      // P2: the furnishing layer — partitions render with the shell wall
      // pieces (base-relative, like shell walls); furniture sits on the
      // raised floor. Same shared derivation as the collision fold.
      const ip = program.interior;
      if (ip !== null) {
        for (const pw of ip.partitions) {
          emit(pw.kind === "door" ? "shell-wall-door" : "shell-wall", pw.x, 0, pw.z, SIDE_ROT[pw.side]);
        }
        for (const fu of ip.furniture) {
          emit(fu.piece, fu.x, fu.level === 0 ? ip.floorTopM : fu.level * H, fu.z, fu.rot);
        }
      }
    } else {
      for (const m of plan.modules) emit(m.module, m.x, m.y, m.z, m.rot, m.wU, m.dU);
    }
    for (const tr of plan.trims) {
      // 2026-07-30 ("greebles go straight through the walls into the house"):
      // trim-stair is a 4m ramp slab tilted 0.62 rad against a step face — its
      // lower half swings ~1.16m through the 0.4m wall. Solid modules swallow
      // that; a hollow interior shows a giant diagonal slab crossing the room.
      // Hollow buildings have REAL interior stairs, so the facade ramp is
      // dropped there (solid buildings keep their decor; streams untouched).
      if (tr.piece === "trim-stair" && plan.shell !== null) continue;
      emit(tr.piece, tr.x, tr.y, tr.z, tr.rot);
    }
    // ── 2026-07-31 style pass: signs on SOLID buildings + family MOTIFS ──────
    // (Hollow buildings emit their sign inside the shell branch above; solid
    // ones anchor the same sign geometry off plan.door.) Faith buildings are
    // crowned by a glowing finial, inns hang a lantern by the door, military
    // posts fly the banner — the type reads from the street.
    {
      const use = buildings[o + 15];
      const d = plan.door;
      const alongX = d.side <= 1;
      // plan.door is ALREADY the door tile's OUTER-FACE centre (doorFor) — the
      // old extra half-module here hung every sign/lantern/banner 2 m off the
      // wall ("signs are floating in front of the walls", 2026-08-02).
      const wallX = d.x;
      const wallZ = d.z;
      if (plan.shell === null) {
        const signId = SIGN_FOR_USE.get(use);
        if (signId !== undefined) {
          emit(signId, wallX + (alongX ? 1.45 : 0), 2.5, wallZ + (alongX ? 0 : 1.45), SIDE_ROT[d.side]);
        }
      }
      if (family === 4) { // faith — spire finial on the highest cell
        let top = plan.modules[0];
        for (const m of plan.modules) if (m.level > top.level) top = m;
        if (top) emit("mot-finial", top.x, (top.level + 1) * LEVEL_H_M + 0.02, top.z, 0);
      }
      if (use === USE_INN) { // lantern on the far door flank, glowing at night
        emit("mot-lantern", wallX - (alongX ? 1.3 : 0), 2.1, wallZ - (alongX ? 0 : 1.3), SIDE_ROT[d.side]);
      }
      if (family === 5) { // military — the banner flies beside the door
        emit("grb-banner", wallX - (alongX ? 1.35 : 0), 3.1, wallZ - (alongX ? 0 : 1.35), SIDE_ROT[d.side]);
      }
    }
    // 2026-07-30 ("don't let banners stick out of windows"): hollow buildings
    // have REAL window openings, and wall greebles (banners/awnings) placed
    // on a window-kind face hang straight through the pane. Skip any wall
    // greeble anchored on a `win` shell-wall edge (its anchor point is the
    // exact edge midpoint the greeble pass used); solid buildings keep their
    // decorative-band behaviour unchanged.
    let winEdges: Set<string> | null = null;
    if (plan.shell !== null) {
      winEdges = new Set();
      const SDX2 = [0, 0, 1, -1], SDZ2 = [1, -1, 0, 0];
      const addWin = (wl: { x: number; z: number; side: 0 | 1 | 2 | 3; kind: string }, level: number): void => {
        if (wl.kind !== "win") return;
        const ex = wl.x + SDX2[wl.side] * plan.unit / 2;
        const ez = wl.z + SDZ2[wl.side] * plan.unit / 2;
        winEdges!.add(`${Math.round(ex * 10)}:${Math.round(ez * 10)}:${level}`);
      };
      for (const wl of plan.shell.walls) addWin(wl, 0);
      for (const wl of plan.shell.upperWalls) addWin(wl, wl.level);
    }
    for (const g of plan.greebles) {
      // Wall greebles sit at mid-storey height on the cell edge.
      const gLevel = Math.floor(g.y / LEVEL_H_M);
      const isWallG = Math.abs(g.y - (gLevel * LEVEL_H_M + LEVEL_H_M / 2)) < 0.01;
      if (winEdges !== null && isWallG
        && winEdges.has(`${Math.round(g.x * 10)}:${Math.round(g.z * 10)}:${gLevel}`)) continue;
      // 2026-07-30 ("a diagonal board hanging out of the roof"): awnings only
      // make sense over the GROUND storey — on an upper wall the tilted plane
      // reads as a floating board spearing the eaves/parapet band. Filtered at
      // the bake (like the window filter above) so the shared greeble stream
      // and its byte-pins stay untouched.
      if (g.greeble === "grb-awning" && isWallG && gLevel > 0) continue;
      emit(g.greeble, g.x, g.y, g.z, g.rot);
    }
    if(program.envelope){
      const doc=program.envelope.asset;
      for(const inst of compileAsset(doc).instances){
        const color=doc.materials[inst.material].color;
        out.push({b,geomKey:geomKeyOf(inst),kind:inst.kind,params:inst.params,
          ...(inst.profile?{profile:inst.profile}:{}),...(inst.deforms?{deforms:inst.deforms}:{}),...(inst.faceting===undefined?{}:{faceting:inst.faceting}),
          glass:false,matrix:mat4Multiply(mB,inst.transform),
          color:new THREE.Color(color).toArray() as [number,number,number]});
      }
    }
  }
  return out;
}

// ── THREE-dependent bake ──────────────────────────────────────────────────────

/** Replicated from proceduralAssetView.ts (module-private there): apply
 * taper/bend to a geometry in the instance's local frame — identical math, so
 * lab, game vehicles, and buildings deform a doc the same way. */
function applyDeforms(geometry: THREE.BufferGeometry,
  deforms: NonNullable<CompiledInstance["deforms"]>): void {
  const AXIS_INDEX = { x: 0, y: 1, z: 2 } as const;
  const positions = geometry.getAttribute("position") as THREE.BufferAttribute;
  geometry.computeBoundingBox();
  const box = geometry.boundingBox!;
  const v = new THREE.Vector3();
  if (deforms.taper) {
    const axis = AXIS_INDEX[deforms.taper.axis];
    const minA = box.min.getComponent(axis), maxA = box.max.getComponent(axis);
    const span = Math.max(1e-6, maxA - minA);
    for (let i = 0; i < positions.count; i++) {
      v.fromBufferAttribute(positions, i);
      const t = (v.getComponent(axis) - minA) / span;
      const scale = 1 + (deforms.taper.factor - 1) * t;
      for (let c = 0; c < 3; c++) if (c !== axis) v.setComponent(c, v.getComponent(c) * scale);
      positions.setXYZ(i, v.x, v.y, v.z);
    }
  }
  if (deforms.bend) {
    const bendAxis = AXIS_INDEX[deforms.bend.axis];
    const alongAxis = deforms.bend.axis === "y" ? 0 : 1;
    geometry.computeBoundingBox();
    const bb = geometry.boundingBox!;
    const center = bb.getCenter(new THREE.Vector3());
    const minA = bb.min.getComponent(alongAxis), maxA = bb.max.getComponent(alongAxis);
    const span = Math.max(1e-6, maxA - minA);
    const axisVec = new THREE.Vector3(); axisVec.setComponent(bendAxis, 1);
    const q = new THREE.Quaternion();
    for (let i = 0; i < positions.count; i++) {
      v.fromBufferAttribute(positions, i);
      const t = (v.getComponent(alongAxis) - minA) / span;
      q.setFromAxisAngle(axisVec, deforms.bend.angle * t);
      v.sub(center).applyQuaternion(q).add(center);
      positions.setXYZ(i, v.x, v.y, v.z);
    }
  }
  positions.needsUpdate = true;
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
}

/** Param-sized geometry per kind (same segment counts as proceduralAssetView so
 * a doc reads identically in the lab and in a settlement; boxes keep 2×2×2
 * segments so taper/bend have vertices to move). Exported for the homestead
 * build-mode ghost, which renders one piece's instances as plain meshes. */
export function buildGeometry(inst: BuildingInstance): THREE.BufferGeometry {
  const p = inst.params;
  let geometry: THREE.BufferGeometry;
  switch (inst.kind) {
    case "box": geometry = new THREE.BoxGeometry(p[0], p[1], p[2], 2, 2, 2); break;
    case "cylinder": geometry = new THREE.CylinderGeometry(p[0], p[1], p[2], 24, 4); break;
    case "sphere": geometry = new THREE.SphereGeometry(p[0], 24, 16); break;
    case "cone": geometry = new THREE.ConeGeometry(p[0], p[1], 24, 4); break;
    case "capsule": geometry = new THREE.CapsuleGeometry(p[0], p[1], 8, 16); break;
    case "torus": geometry = new THREE.TorusGeometry(p[0], p[1], 12, 32); break;
    case "lathe": {
      geometry = proceduralLathe(inst);
      break;
    }
  }
  if (inst.deforms) applyDeforms(geometry, inst.deforms);
  bakeVertexAO(geometry);
  return geometry;
}

/**
 * Bake a per-vertex tone (fake ambient occlusion) into the geometry so flat box
 * faces stop reading as uniform cardboard panels — the reason buildings looked
 * artificial next to the flora/rocks, whose faceted, non-indexed meshes carry a
 * natural tonal gradient. A box wall is ONE flat face (one normal → one Lambert
 * value across its whole area); this adds a VERTICAL sky-occlusion gradient
 * (darker at the base, lighter toward the top, where a surface sees more open
 * sky) plus a tighter contact-shadow band at the very bottom. Local +Y is the
 * module's up for every structure/greeble piece, so the gradient reads as real
 * height shading. Grayscale, so it MULTIPLIES the per-instance building colour
 * (vertexColor × instanceColor × white material) — no hue shift, no runtime cost
 * (baked once per cached geometry). Glass ignores it (its MeshBasic material has
 * vertexColors off).
 */
function bakeVertexAO(geometry: THREE.BufferGeometry): void {
  const pos = geometry.getAttribute("position");
  geometry.computeBoundingBox();
  const bb = geometry.boundingBox!;
  const minY = bb.min.y;
  const spanY = Math.max(1e-6, bb.max.y - minY);
  const colors = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const t = (pos.getY(i) - minY) / spanY;      // 0 base → 1 top
    let tone = 0.66 + 0.34 * (t * t * (3 - 2 * t)); // smoothstep sky gradient
    if (t < 0.16) tone *= 0.82 + (t / 0.16) * 0.18; // contact-shadow band at grade
    colors[i * 3] = tone; colors[i * 3 + 1] = tone; colors[i * 3 + 2] = tone;
  }
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
}

/**
 * Group instances by geometry bucket and bake InstancedMeshes.
 *  - non-glass: ONE shared white MeshLambertMaterial + per-instance setColorAt
 *    (the settlement renderer's wall convention — its materials are plain
 *    Lambert with no stylizedShading hook, so this already matches the world;
 *    if settlements ever adopt addStylizedShading, apply it to `lambert` here).
 *    The shared Lambert is returned as `wallMat` so the renderer can keep it as
 *    `v.buildingMat` — the night pass pokes its emissive warm (Task 9 wiring).
 *  - glass (material 3): ONE shared MeshBasicMaterial across ALL glass buckets
 *    (colour seeded from the first glass instance), returned in `glassMats`.
 *    Shared, not per-bucket, because the renderer's night pass drives the mat
 *    colour every frame (v.windowMat poke) — per-bucket mats would leave every
 *    bucket after the first permanently dead at night, and the poke overwrites
 *    any baked per-bucket colour anyway (same one-windowMat convention as the
 *    legacy kit path).
 */
export function bakeBuildingMeshes(instances: BuildingInstance[]): {
  meshes: THREE.InstancedMesh[]; glassMats: THREE.MeshBasicMaterial[];
  wallMat: THREE.MeshLambertMaterial;
} {
  const groups = new Map<string, BuildingInstance[]>();
  for (const inst of instances) {
    const key = `${inst.glass ? "g" : "s"}|${inst.geomKey}`;
    const list = groups.get(key);
    if (list) list.push(inst); else groups.set(key, [inst]);
  }
  const meshes: THREE.InstancedMesh[] = [];
  const glassMats: THREE.MeshBasicMaterial[] = [];
  // vertexColors ON so the baked AO gradient (bakeVertexAO) modulates every wall;
  // it multiplies with the per-instance setColorAt building colour.
  const lambert = new THREE.MeshLambertMaterial({ color: 0xffffff, vertexColors: true });
  // Painted light (world/stylizedShading.ts): town walls are a large, flat-lit
  // share of any settlement frame, so leaving them on plain Lambert is exactly
  // where the painted world would visibly stop. No rim/sheen — those were the
  // parts that read wrong on architecture; this is the banding only.
  addStylizedShading(lambert, { weatherGround: true });
  const mtx = new THREE.Matrix4();
  const col = new THREE.Color();
  for (const list of groups.values()) {
    const first = list[0];
    const geometry = buildGeometry(first);
    let material: THREE.Material;
    if (first.glass) {
      if (glassMats.length === 0) {
        // 2026-07-30: SEE-THROUGH glass ("make windows actually glass") —
        // transparent by day so interiors read through the panes;
        // depthWrite off so the two-layer shell window bands don't self-
        // occlude. The renderer's night poke raises color AND opacity
        // toward the warm lamplit pane.
        glassMats.push(new THREE.MeshBasicMaterial({
          color: new THREE.Color(first.color[0], first.color[1], first.color[2]),
          transparent: true,
          opacity: 0.38,
          depthWrite: false,
        }));
      }
      material = glassMats[0];
    } else {
      material = lambert;
    }
    const mesh = new THREE.InstancedMesh(geometry, material, list.length);
    if (!first.glass) mesh.userData.footstepSurfaces = list.map(inst => inst.footstepSurface ?? 'stone');
    for (let i = 0; i < list.length; i++) {
      mesh.setMatrixAt(i, mtx.fromArray(list[i].matrix));
      if (!first.glass) {
        mesh.setColorAt(i, col.setRGB(list[i].color[0], list[i].color[1], list[i].color[2]));
      }
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    mesh.computeBoundingSphere();
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    meshes.push(mesh);
  }
  return { meshes, glassMats, wallMat: lambert };
}
